import React from 'react';
import clsx from 'clsx';
import { EditorStringModel } from '../../Types/EditorString';
import { LinkModel } from '../../Types/LinkModel';
import DivOrAnchor from '../../Components/DivOrAnchor';
import { MediaModel } from '../../Types/MediaModel';
import EditorString from '../../Components/EditorString/EditorString';

interface StyledButton {
  buttonLink: LinkModel;
  buttonStyle: string;
}

export interface BannerBlockViewModel {
  systemId: string;
  desktopMedia?: MediaModel;
  mobileMedia?: MediaModel;
  overlay?: string;
  textPlacement?: string;
  content?: EditorStringModel;
  styledButtons?: StyledButton[];
  bannerLink?: LinkModel;
  disableRoundedCorners: boolean;
  disableHoverEffect: boolean;
  backgroundColor?: string;
}

interface BannerBlockProps extends BannerBlockViewModel {
  className?: string;
  inProductListing?: boolean;
}

const BannerBlock = ({
  desktopMedia,
  mobileMedia,
  overlay,
  textPlacement,
  content,
  styledButtons,
  bannerLink,
  disableRoundedCorners,
  disableHoverEffect,
  backgroundColor,
  className,
  inProductListing = false,
}: BannerBlockProps) => {
  const hasMobileSpecificMedia =
    !!mobileMedia &&
    (!!mobileMedia.imageUrl ||
      !!mobileMedia.video?.url ||
      !!mobileMedia.videoUrl);
  const isLinkedBanner = !!bannerLink?.href;

  return (
    <DivOrAnchor
      isAnchor={isLinkedBanner}
      className={clsx(
        'group block',
        className,
        inProductListing && 'h-full w-full aspect-video',
        !disableRoundedCorners && 'rounded'
      )}
      href={bannerLink?.href}
      style={{ backgroundColor }}
    >
      <div
        className={clsx('relative overflow-hidden will-change-transform', {
          rounded: !disableRoundedCorners,
          'h-full': inProductListing,
        })}
      >
        {/* Render video or image based on condition */}
        {desktopMedia?.video && (
          <video
            src={desktopMedia?.video.url}
            className={clsx('pointer-events-none w-full', {
              rounded: !disableRoundedCorners,
              'hidden md:block': hasMobileSpecificMedia,
              'overflow-hidden transition duration-500 group-hover:scale-105':
                isLinkedBanner && !disableHoverEffect,
              'h-full object-cover': inProductListing,
            })}
            style={{ clipPath: 'inset(1px 1px)' }}
            autoPlay
            muted
            loop
            playsInline
          ></video>
        )}
        {desktopMedia?.imageUrl && (
          <div
            className={clsx('pointer-events-none w-full', {
              rounded: !disableRoundedCorners,
              'hidden md:block': hasMobileSpecificMedia,
              'overflow-hidden transition duration-500 group-hover:scale-105':
                isLinkedBanner && !disableHoverEffect,
              'h-full': inProductListing,
            })}
          >
            <img
              src={desktopMedia.imageUrl}
              alt="Desktop Media"
              className={clsx({
                'h-full w-full object-cover': inProductListing,
              })}
            />
          </div>
        )}

        {/* Render mobile video or image based on condition */}
        {mobileMedia?.video?.url && (
          <video
            src={mobileMedia?.video?.url}
            className={clsx('pointer-events-none block w-full md:hidden', {
              rounded: !disableRoundedCorners,
              'h-full': inProductListing,
            })}
            style={{ clipPath: 'inset(1px 1px)' }}
            autoPlay
            muted
            playsInline
            loop
          ></video>
        )}
        {mobileMedia?.imageUrl && (
          <div
            className={clsx('block w-full md:hidden', {
              rounded: !disableRoundedCorners,
              'h-full': inProductListing,
            })}
          >
            <img
              src={mobileMedia.imageUrl}
              alt="Mobile Media"
              className={clsx({
                'h-full w-full object-cover': inProductListing,
              })}
            />
          </div>
        )}

        {/* Render text content and buttons */}
        <div className="h-full w-full">
          <div
            className={clsx(
              `${
                textPlacement === 'center'
                  ? 'items-center justify-center'
                  : 'items-end justify-start'
              } absolute bottom-0 left-0 right-0 top-0 z-20 flex overflow-hidden p-4 lg:p-6`
            )}
          >
            <div
              className={clsx('flex flex-col', {
                'justify-center': textPlacement === 'center',
              })}
            >
              <div className="[&_.editor-string]:max-w-[568px]">
                <EditorString {...content} />
              </div>
              {styledButtons && styledButtons.length > 0 && (
                <div
                  className={clsx(
                    'mt-6 flex flex-wrap gap-4',
                    textPlacement === 'center' && 'justify-center'
                  )}
                >
                  {styledButtons.map((button, index) => (
                    <React.Fragment key={index}>
                      {isLinkedBanner ? (
                        <span
                          className={clsx(
                            'whitespace-nowrap',
                            button.buttonStyle ??
                              'btn btn--large btn--white'
                          )}
                        >
                          {button.buttonLink.text}
                        </span>
                      ) : (
                        <a
                          className={clsx(
                            'whitespace-nowrap',
                            button.buttonStyle ??
                              'btn btn--large btn--white'
                          )}
                          href={button.buttonLink.href}
                        >
                          {button.buttonLink.text}
                        </a>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Render overlay */}
        {overlay && (
          <div
            className={clsx(
              'pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-10',
              !disableRoundedCorners && 'rounded',
              overlay === 'overlay-light' && 'bg-opacity-black-06',
              overlay === 'overlay-dark' && 'bg-opacity-black-30',
              inProductListing && 'h-full'
            )}
          ></div>
        )}
      </div>
    </DivOrAnchor>
  );
};

export default BannerBlock;
